import type { Option } from '@/domain/base';
import { ListingStatus, type Unit } from '@/domain/units';
import { useEffect, useMemo, useState, type FC } from 'react';

interface Filters {
  bedroomCount?: number;
  availableOnly?: boolean;
}

interface Props {
  units: Unit[] | undefined;
  setFilteredUnits: (units: Unit[]) => void;
}

const FloorPlansFilters: FC<Props> = ({ units, setFilteredUnits }) => {
  const [filters, setFilters] = useState<Filters>({});

  const bedRoomOptions = useMemo<Option<number | undefined>[]>(() => {
    const options = (units ?? [])
      .map((unit) => ({
        value: unit.bedroomCount,
        label: `${unit.bedroomCount} Beds`,
      }))
      .reduce((acc, current) => {
        if (!acc.find((option) => option.value === current.value)) {
          acc.push(current);
        }
        return acc;
      }, [] as Option<number>[]);
    options.sort((a, b) => a.value - b.value);
    return [{ value: undefined, label: 'All' }, ...options];
  }, [units]);

  const filteredUnits = useMemo(
    () =>
      (units ?? []).filter((unit) => {
        if (!!filters.bedroomCount && unit.bedroomCount !== filters.bedroomCount) {
          return false;
        }

        if (filters.availableOnly && unit.listingStatus !== ListingStatus.ACTIVE) {
          return false;
        }

        return true;
      }),
    [units, filters],
  );

  useEffect(() => {
    setFilteredUnits(filteredUnits);
  }, [filteredUnits, setFilteredUnits]);

  return (
    <div className="bg-white p-6 rounded-none border border-gray-200 mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Floor Plans</h3>
      <div className="flex flex-col md:flex-row gap-4">
        {/* Bedroom Filter */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
          <div className="flex flex-wrap gap-2">
            {bedRoomOptions.map((option) => (
              <button
                key={`bedrooms-option-${option.label}`}
                onClick={() => setFilters((prev) => ({ ...prev, bedroomCount: option.value }))}
                className={`px-4 py-2 rounded-none font-medium transition-colors ${
                  filters.bedroomCount === option.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Availability Filter */}
        <div className="flex items-end">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.availableOnly}
              onChange={(e) => setFilters((prev) => ({ ...prev, availableOnly: e.target.checked }))}
              className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Show Available Only</span>
          </label>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Showing {filteredUnits.length} of {units?.length} floor plans
      </div>
    </div>
  );
};

export default FloorPlansFilters;
